import React, { useMemo } from 'react';
import { Badge, CopyButton } from '../ui';
import {
  HEALTH_LABEL, KIND_GROUP_LABEL, KIND_LABEL, ago, healthWithReasons,
} from '../../observability/contract';
import { loadServiceMetrics } from '../../observability/client';
import usePlatformData from '../../observability/usePlatformData';
import { HealthBadge } from './OverviewPanel';
import ServiceDetail from './ServiceDetail';
import SourceWarnings from './SourceWarnings';
import { gib, rate, vcpu } from './units';

function ServiceList({ services, selected, onSelect }) {
  const groups = ['proxy', 'bridge', 'core'];
  return (
    <nav className="service-nav hide-sm" aria-label="Services">
      {groups.map((kind) => {
        const list = services.filter(s => s.kind === kind);
        if (!list.length) return null;
        return (
          <div key={kind} className="service-nav-group">
            <div className="overline">{KIND_GROUP_LABEL[kind]}</div>
            {list.map((s) => {
              const { health } = healthWithReasons(s);
              return (
                <button key={s.id} type="button"
                        className={`service-nav-item${s.id === selected ? ' is-active' : ''}`}
                        aria-current={s.id === selected ? 'true' : undefined}
                        onClick={() => onSelect(s.id)}>
                  <span className={`dot health-dot-${health}`} aria-hidden="true" />
                  <span className="truncate">{s.id}</span>
                  <span className="sr-only">, {HEALTH_LABEL[health]}</span>
                  <span className="service-nav-count num">{s.running}/{s.desired}</span>
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}

const KIND_RANK = { proxy: 0, bridge: 1, core: 2 };

export default function ServicesPanel({ services: unordered, selected, onSelect, range, refreshMs }) {
  // Same order as the list on the left, so the default selection is the first
  // entry there rather than whichever service the API happened to list first.
  const services = useMemo(() => [...unordered]
    .sort((a, b) => (KIND_RANK[a.kind] ?? 9) - (KIND_RANK[b.kind] ?? 9)), [unordered]);
  const svc = services.find(s => s.id === selected) || services[0];
  const svcId = svc?.id;
  const { data, error, loading } = usePlatformData(
    () => (svcId ? loadServiceMetrics(svcId, range) : Promise.resolve(null)), [svcId, range],
    refreshMs ? { intervalMs: refreshMs } : undefined);
  const replicas = useMemo(() => data?.replicas || [], [data]);

  if (!svc) return null;
  const { health, reasons } = healthWithReasons(svc);

  return (
    <div className="platform-split">
      <ServiceList services={services} selected={svc.id} onSelect={onSelect} />

      <div className="min-w-0">
        <div className="show-sm mb-3">
          <label htmlFor="svc-pick" className="form-label">Service</label>
          <select id="svc-pick" className="form-input" value={svc.id} onChange={e => onSelect(e.target.value)}>
            {services.map(s => <option key={s.id} value={s.id}>{s.id}</option>)}
          </select>
        </div>

        <div className="card mb-4">
          <div className="row row-wrap gap-3 items-start">
            <div className="grow min-w-0">
              <div className="row row-wrap gap-2">
                <h2 className="service-title">{svc.id}</h2>
                <HealthBadge health={health} />
                <Badge tone="outline">{KIND_LABEL[svc.kind]}</Badge>
              </div>
              <p className="text-muted mt-1 mb-0">{svc.role}</p>
            </div>
          </div>
          {svc.note && <div className="notice notice-info mt-3"><div className="notice-body">{svc.note}</div></div>}
          {reasons.length > 0 && health !== 'ok' && health !== 'idle' && (
            <div className={`notice ${health === 'critical' ? 'notice-danger' : 'notice-warn'} mt-3`}>
              <div className="notice-body">{reasons.join(' · ')}</div>
            </div>
          )}
          <dl className="kv mt-4">
            <dt>ECS service</dt>
            <dd><span className="row gap-1"><span className="mono text-sm">{svc.ecs_service}</span>
              <CopyButton value={svc.ecs_service} label="Copy ECS service name" /></span></dd>
            <dt>Runtime</dt>
            <dd>{svc.runtime === 'ec2' ? 'EC2, host networking' : 'Fargate'}{svc.routed === false ? ' · not routed' : ''}</dd>
            <dt>Reservation</dt>
            <dd>{vcpu(svc.cpu_units)} · {gib(svc.mem_mib)} per task</dd>
            <dt>Replicas</dt>
            <dd>{svc.running} running of {svc.desired} desired{svc.pending ? `, ${svc.pending} pending` : ''}
              {replicas.length > svc.running ? ` · ${replicas.length} copies ran in this window` : ''}</dd>
            <dt>Image</dt>
            <dd>
              <span className="mono text-sm">{svc.image?.tag}</span>{' '}
              {svc.image?.stale
                ? <Badge tone="warn">older than :latest</Badge>
                : <span className="text-muted">matches :latest</span>}
            </dd>
            <dt>Deployment</dt>
            <dd>{svc.deployment?.state === 'rolling' ? 'rolling out' : svc.deployment?.state || '—'}
              {svc.deployment?.since ? `, since ${ago(svc.deployment.since)}` : ''}</dd>
            {svc.requests_per_min != null && (
              <><dt>Traffic</dt><dd>{rate(svc.requests_per_min)} requests/min, averaged over the last 10 min</dd></>
            )}
          </dl>
        </div>

        {error && (
          <div className="notice notice-danger mb-4" role="status">
            <div className="notice-body">Could not load metrics for {svc.id}: {error}</div>
          </div>
        )}
        <SourceWarnings warnings={data?.warnings} />

        {/* The copies and their charts, shared with the load tests' report. Keyed by the
            service, so each chart's copy view starts over on another service. */}
        <ServiceDetail key={svc.id} data={data} loading={loading} range={range} />
      </div>
    </div>
  );
}
